import React, { useState, useEffect, useRef } from "react";
import { Shield, Menu, X, Copy, Check, Scale } from "lucide-react";

export interface PolicySection {
  id: string;
  title: string;
  content: string | string[];
}

export interface PolicyData {
  lastUpdated: string;
  sections: PolicySection[];
}

export const POLICY_CONTENT: PolicyData = {
  lastUpdated: "December 5, 2025",
  sections: [
    {
      id: "introduction",
      title: "1. Introduction",
      content: [
        'Welcome to H2 Wallet. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application, browser extension, and related services (collectively, the "Service").',
        "By accessing or using H2 Wallet, you agree to the terms of this Privacy Policy. If you do not agree with the terms of this policy, please do not access the application.",
      ],
    },
    {
      id: "collection",
      title: "2. Information We Collect",
      content: [
        "We prioritize the minimization of data collection. However, to provide our Service, we may collect and process the following types of information:",
        "**Information You Provide to Us:** We collect information that you voluntarily provide, such as when you contact customer support. This may include your email address and the content of your message.",
        "**Automatically Collected Information:** When you use H2 Wallet, we may automatically collect certain device information, including your IP address, device type, operating system version, and unique device identifiers. We use this strictly for diagnostics, security, and optimizing the user experience.",
        "**Blockchain Data:** Please be aware that your public wallet address and any transactions you perform are recorded on the public blockchain. This information is publicly available and immutable. We do not control this data.",
      ],
    },
    {
      id: "private-keys",
      title: "3. Private Keys and Seed Phrases",
      content: [
        "**Crucial:** H2 Wallet is a non-custodial wallet. We do not have access to, nor do we store, your Private Keys, Secret Recovery Phrases (Seed Phrases), or passwords.",
        "Your private keys are encrypted and stored locally on your device. You are solely responsible for keeping your credentials secure. We cannot recover your wallet if you lose your Secret Recovery Phrase.",
      ],
    },
    {
      id: "usage",
      title: "4. How We Use Your Information",
      content: [
        "We use the information we collect for the following purposes:",
        "• To operate and maintain the H2 Wallet Service.",
        "• To improve user experience and fix technical issues.",
        "• To detect and prevent fraud, security breaches, and malicious activity.",
        "• To comply with legal obligations and regulatory requirements.",
      ],
    },
    {
      id: "sharing",
      title: "5. Information Sharing",
      content: [
        "We do not sell your personal data. We may share information in the following limited circumstances:",
        "**Service Providers:** We may share non-personal, aggregated data with third-party vendors (such as RPC providers or infrastructure services) necessary to connect H2 Wallet to blockchain networks.",
        "**Legal Requirements:** We may disclose information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).",
        "**Business Transfers:** If we are involved in a merger, acquisition, or asset sale, your information may be transferred as part of that transaction.",
      ],
    },
    {
      id: "third-party",
      title: "6. Third-Party Links and Services",
      content: [
        "H2 Wallet may contain links to third-party websites, decentralized applications (dApps), or services that are not owned or controlled by us. We are not responsible for the content, privacy policies, or practices of any third-party services. We strongly advise you to read the terms and conditions and privacy policies of any third-party web sites or services that you visit.",
      ],
    },
    {
      id: "security",
      title: "7. Data Security",
      content: [
        "We implement appropriate technical and organizational security measures to protect your information. However, please remember that no method of transmission over the Internet, or method of electronic storage, is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.",
      ],
    },
    {
      id: "children",
      title: "8. Children's Privacy",
      content: [
        "Our Service is not intended for use by children under the age of 18. We do not knowingly collect personally identifiable information from children. If we become aware that we have collected personal data from a child without verification of parental consent, we take steps to remove that information from our servers.",
      ],
    },
    {
      id: "updates",
      title: "9. Changes to This Policy",
      content: [
        'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.',
      ],
    },
    {
      id: "contact",
      title: "10. Contact Us",
      content: [
        "If you have any questions about this Privacy Policy, please contact us at:",
        "Email: h2chain@outlook.com",
      ],
    },
  ],
};

const PrivicyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(
    POLICY_CONTENT.sections[0].id,
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Refs for intersection observer
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px",
      threshold: 0,
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el instanceof Element) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: "smooth",
      });
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  const handleCopyText = () => {
    // Generate plain text version
    const textBody = POLICY_CONTENT.sections
      .map((section) => {
        const content = Array.isArray(section.content)
          ? section.content.join("\n\n")
          : section.content;
        return `${section.title.toUpperCase()}\n\n${content}`;
      })
      .join("\n\n-------------------\n\n");

    const header = `PRIVACY POLICY FOR H2 WALLET\nLast Updated: ${POLICY_CONTENT.lastUpdated}\n\n`;

    navigator.clipboard.writeText(header + textBody).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col relative z-[99] h-screen overflow-auto">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
              <Shield size={20} fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">
              H2 Wallet
            </span>
            <span className="hidden sm:inline-block h-4 w-px bg-slate-300 mx-2"></span>
            <span className="hidden sm:inline-block text-sm font-medium text-slate-500 uppercase tracking-wider">
              Privacy Policy
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleCopyText}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy Text"}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-md"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* Sidebar Navigation (Desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-4 px-3">
                Contents
              </h3>
              <nav className="space-y-1">
                {POLICY_CONTENT.sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-all duration-200 ${
                      activeSection === section.id
                        ? "bg-blue-50 text-blue-700 font-medium translate-x-1"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>

              <div className="mt-8 px-3 pt-8 border-t border-slate-200">
                <p className="text-xs text-slate-400">
                  Last Updated: <br />
                  <span className="text-slate-700 font-medium">
                    {POLICY_CONTENT.lastUpdated}
                  </span>
                </p>
              </div>
            </div>
          </aside>

          {/* Mobile Navigation Drawer */}
          {mobileMenuOpen && (
            <div className="fixed inset-0 z-40 lg:hidden">
              <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={() => setMobileMenuOpen(false)}
              ></div>
              <div className="absolute right-0 top-16 bottom-0 w-64 bg-white shadow-xl border-l border-slate-200 overflow-y-auto">
                <div className="p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
                    Table of Contents
                  </h3>
                  <nav className="space-y-1">
                    {POLICY_CONTENT.sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-3 text-sm rounded-md transition-colors ${
                          activeSection === section.id
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10 lg:p-12">
              <div className="mb-10 pb-8 border-b border-slate-100">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
                  Privacy Policy
                </h1>
                <p className="text-lg text-slate-600 leading-relaxed">
                  This document outlines how H2 Wallet collects, uses, and
                  protects your data. Transparency is core to our mission of
                  decentralized finance.
                </p>
              </div>

              <div className="space-y-16">
                {POLICY_CONTENT.sections.map((section) => (
                  <section
                    // @ts-ignore
                    ref={(el) => (sectionRefs.current[section.id] = el)}
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-28"
                  >
                    <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                      {section.title}
                    </h2>
                    <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-4">
                      {Array.isArray(section.content) ? (
                        section.content.map((paragraph, idx) => (
                          <p
                            key={idx}
                            dangerouslySetInnerHTML={{
                              __html: paragraph.replace(
                                /\*\*(.*?)\*\*/g,
                                '<strong class="text-slate-800 font-semibold">$1</strong>',
                              ),
                            }}
                          />
                        ))
                      ) : (
                        <p>{section.content}</p>
                      )}
                    </div>
                  </section>
                ))}
              </div>

              {/* Footer inside card */}
              <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500">
                <p>
                  © {new Date().getFullYear()} H2 Wallet Team. All rights
                  reserved.
                </p>
                <div className="flex gap-4">
                  <span className="flex items-center gap-1 cursor-not-allowed opacity-50">
                    <Scale size={14} /> Legal
                  </span>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivicyPolicy;
